import type { CSSProperties } from 'react'
import type { CurrencyCode } from '@/lib/money'
import { cn } from '@/lib/cn'
import styles from './CurrencyBadge.module.css'

/**
 * A coin per currency. Colours are tokens (tokens.css, --pb-coin-*) at one
 * shared lightness, so every coin weighs the same and its label passes
 * contrast on both stops. The dollar is the home currency: it takes the brand
 * accent and dark ink, so the coin follows the brand.
 */
function gradient(currency: CurrencyCode): string {
  if (currency === 'USD') return 'linear-gradient(145deg, var(--pb-accent-soft), var(--pb-accent))'
  const key = currency.toLowerCase()
  return `linear-gradient(145deg, var(--pb-coin-${key}-from), var(--pb-coin-${key}-to))`
}

export function CurrencyBadge({
  currency,
  size = 40,
  className,
}: {
  readonly currency: CurrencyCode
  readonly size?: number | undefined
  readonly className?: string | undefined
}) {
  const long = currency.length > 3
  return (
    <span
      className={cn(styles.badge, currency === 'USD' && styles.onAccent, long && styles.long, className)}
      style={
        {
          '--coin-size': `${size}px`,
          background: gradient(currency),
        } as CSSProperties
      }
      aria-hidden="true"
    >
      <span className={styles.code}>{currency}</span>
    </span>
  )
}
